import { useState } from "react";
import { useEffect } from "react";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import { createNewContact } from "../../data/omniDataModel";
import { deepCopyObject, isValidEmail } from "../../utils/helpers";
import OmniAssessmentValueTextFab from "../ui/omniAssessmentValueTextFab";
import OmniPhoneNumberField from "../ui/omniPhoneNumberField";
import OmniTextField from "../ui/omniTextField";

const getAnswerTextAsHTML = text => {
  return { __html: `${text}` };
};

const hydrateExistingContact = contact => {
  let newContact = createNewContact();
  newContact.contactType = constants.contactTypes.primaryCaregiver;

  contact = contact.split(",");

  contact.forEach((element, index) => {
    contact[index] = element.trim();
  });

  contact.map(element => {
    return element.trim();
  });

  newContact.contactName = contact[0];
  newContact.mobilePhone = contact[1];
  newContact.email = contact[2];

  return newContact;
};

const QuestionCaregiverDisplay = props => {
  const { question, onAnswerSelected, startNextTimer } = props;

  const theme = useTheme();

  const defaultDelay = 350;

  const [contact, setContact] = useState(null);
  const [nameValid, setNameValid] = useState(false);
  const [mobilePhoneValid, setMobilePhoneValid] = useState(false);
  const [emailValid, setEmailValid] = useState(false);

  const [unsure, setUnsure] = useState(false);

  useEffect(() => {
    if (contact == null) {
      setNameValid(false);
      setMobilePhoneValid(false);
      setEmailValid(false);
    } else {
      contact.contactName != null && contact.contactName.length > 0
        ? setNameValid(true)
        : setNameValid(false);

      isValidEmail(contact.email) ? setEmailValid(true) : setEmailValid(false);

      contact.mobilePhone && RegExp(/^\D*(\d\D*){10}$/).exec(contact.mobilePhone)
        ? setMobilePhoneValid(true)
        : setMobilePhoneValid(false);
    }
  }, [contact]);

  const updateContactODM = event => {
    try {
      setUnsure(false);
      let newContact = deepCopyObject(contact);
      if (newContact === "unsure") {
        newContact = createNewContact();
        newContact.contactType = constants.contactTypes.primaryCaregiver;
      }
      newContact[event.target.id] = event.target.value;
      setContact(deepCopyObject(newContact));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (question.answerValue == null) {
      setUnsure(false);
      let newContact = createNewContact();
      newContact.contactType = constants.contactTypes.primaryCaregiver;
      setContact(newContact);
    } else if (question.answerValue && question.answerValue[0] === "unsure") {
      setUnsure(true);
      setContact(deepCopyObject(question.answerValue[0]));
    } else {
      setUnsure(false);
      setContact(hydrateExistingContact(deepCopyObject(question.answerValue[0])));
    }
  }, [question]);

  useEffect(() => {
    if (nameValid && (emailValid || mobilePhoneValid)) {
      setUnsure(false);
      onAnswerSelected([`${contact.contactName}, ${contact.mobilePhone}, ${contact.email}`]);
    } else if (!unsure) {
      onAnswerSelected(null);
    }
  }, [unsure, nameValid, emailValid, mobilePhoneValid, contact, onAnswerSelected]);

  //Name
  //email or /phone
  //Relationship?
  return (
    <Grid container item direction="row" spacing={2} justifyContent="center">
      {contact && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              marginLeft: theme.spacing(2),
              marginRight: theme.spacing(2),
            }}
          >
            <OmniTextField
              key={`answerOptionQuestion${question.omniID}HF`}
              name={uuid()}
              value={contact.contactName}
              label={"Name"}
              type="textInput"
              id={"contactName"}
              error={!nameValid}
              onChange={event => {
                updateContactODM(event);
              }}
              onFocus={event => event.target.select()}
            />

            <Box sx={{ width: "220px", marginLeft: theme.spacing(1) }}>
              <OmniPhoneNumberField
                label="Mobile Number"
                type="textInput"
                name={uuid()} //prevents AutoComplete Suggestions
                variant="outlined"
                id={"mobilePhone"}
                value={contact.mobilePhone}
                error={!mobilePhoneValid}
                onChange={event => {
                  updateContactODM(event);
                }}
                onFocus={event => event.target.select()}
                placeholder={"Your Phone Number"}
                required
              />
            </Box>
          </Box>
        </Grid>
      )}
      {contact && (
        <Grid container item spacing={1}>
          <Grid
            item
            xs={12}
            sx={{
              marginLeft: theme.spacing(2),
              marginRight: theme.spacing(2),
            }}
          >
            <OmniTextField
              label="Email"
              type="textField"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              id={"email"}
              value={contact.email}
              error={!emailValid}
              onChange={event => {
                updateContactODM(event);
              }}
              placeholder={"Primary Email Address"}
              required
            />
          </Grid>
        </Grid>
      )}

      {contact && (
        <Grid item xs={11} sx={{ marginTop: theme.spacing(2) }}>
          <Grid
            id={`questionGrid1`}
            container
            item
            xs={11}
            justifyContent="center"
            spacing={2}
            sx={{ marginLeft: "4px" }}
          >
            <Tooltip title={question.answerOptions[0].helpText || ""} placement="left">
              <>
                <OmniAssessmentValueTextFab
                  key={`answerOption${uuid()}`}
                  color="primary"
                  size="large"
                  variant={"outlined"}
                  onClick={e => {
                    e.stopPropagation();
                    setUnsure(true);
                    onAnswerSelected(["unsure"]);
                    startNextTimer(defaultDelay);
                  }}
                  sx={{ width: "100%", "&:hover": { color: theme.palette.primary.contrastText } }}
                >
                  <Grid
                    container
                    justifyContent="center"
                    direction="row"
                    sx={{ width: "100%", alignItems: "center" }}
                  >
                    <Grid item justifySelf="center" xs>
                      <Typography
                        variant="button"
                        dangerouslySetInnerHTML={getAnswerTextAsHTML("I don't know")}
                      />
                    </Grid>
                    {unsure && (
                      <CheckCircleRoundedIcon
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 16,
                          maxWidth: "32px",
                          maxHeight: "32px",
                          alignSelf: "baseline",
                        }}
                      />
                    )}
                  </Grid>
                </OmniAssessmentValueTextFab>
              </>
            </Tooltip>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default QuestionCaregiverDisplay;
